import type { Api } from "../../../services/api";
import type { ApiPodcastModel } from "../../../domains/podcast/hooks/useGetPodcastsListQuery";
import type { ForYouFeedItem } from "../../../domains/forYou/hooks/useGetForYouRecommendedContentQuery";
import { gaPushEvent } from "../base.analytics";
import {
  AnalyticsItemReturnData,
  gaEventViewItemList,
} from "../common.analytics";
import { convertArticleToItemData } from "./article.analytics";
import { convertInfographicToItemData } from "./infographic.analytics";

import { convertPodcastToItemData } from "./podcast.analytics";
import { convertVideoToItemData } from "./video.analytics";

enum ContentEvent {
  CONTENT_LIKE = "content_like",
  CONTENT_SAVE = "content_save",
  CONTENT_SHARE = "content_share",
}

type ContentListItem =
  Api["get"]["/webapp/public/content"]["output"]["body"]["docs"][number];
type ArticleListItem =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];
type RoomListItem =
  Api["get"]["/webapp/private/content/last-in-user-room"]["output"]["body"][number];
type LibraryListItem = ArticleListItem | ContentListItem;
type FeedListItem = ForYouFeedItem;

type ContentEventTopLevelProps = {
  language?: string;
  origin?: string;
};

export function gaEventContentLike(
  itemData: AnalyticsItemReturnData,
  props?: ContentEventTopLevelProps
) {
  gaPushEvent(ContentEvent.CONTENT_LIKE, {
    ...props,
    ecommerce: { items: [itemData] },
  });
}

export function gaEventContentSave(
  itemData: AnalyticsItemReturnData,
  props?: ContentEventTopLevelProps
) {
  gaPushEvent(ContentEvent.CONTENT_SAVE, {
    ...props,
    ecommerce: { items: [itemData] },
  });
}

export function gaEventContentShare(
  itemData: AnalyticsItemReturnData,
  props?: ContentEventTopLevelProps
) {
  gaPushEvent(ContentEvent.CONTENT_SHARE, {
    ...props,
    ecommerce: { items: [itemData] },
  });
}

type MixedContentListPayload = {
  content: Array<LibraryListItem | FeedListItem | RoomListItem>;
  podcasts?: ApiPodcastModel[];
  listName: string;
  environmentParams?: Record<string, unknown>;
};

function convertAnyContentToItemData(
  content: LibraryListItem | FeedListItem | RoomListItem,
  listName: string
): AnalyticsItemReturnData | null {
  switch (content.content_format) {
    case "Article": {
      return convertArticleToItemData({
        article: content,
        listName,
      });
    }
    case "Nectar": {
      return null;
    }
    case "Video": {
      return convertVideoToItemData({
        video: content,
        listName,
      });
    }
    case "Infographic": {
      return convertInfographicToItemData({
        infographic: content,
        listName,
      });
    }
    case "Audio": {
      return null;
    }
    default: {
      throw new Error(content satisfies never);
    }
  }
}

export function gaEventViewItemListMixedContent(
  payload: MixedContentListPayload
) {
  const contentItems: AnalyticsItemReturnData[] = [];
  for (const content of payload.content) {
    const item = convertAnyContentToItemData(content, payload.listName);
    if (item !== null) {
      contentItems.push(item);
    }
  }
  const podcastItems = (payload.podcasts ?? []).map((podcast) =>
    convertPodcastToItemData({ podcast, listName: payload.listName })
  );
  gaEventViewItemList(
    [...contentItems, ...podcastItems],
    payload.environmentParams
  );
}
